import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { pendingRecipes } from '../api/client';

// Maintainer queue for anonymous public submissions (#520). Admin
// approves → recipe joins their kitchen and lands in the public
// catalog. Reject → deleted.
export default function PendingRecipesPage() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [working, setWorking] = useState(null);
  const [error, setError] = useState(null);

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const { data } = await pendingRecipes.list();
      setItems(data.recipes || []);
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const handleApprove = async (id) => {
    setWorking(id);
    try {
      await pendingRecipes.approve(id);
      await load();
    } catch (err) {
      alert('Approve failed: ' + (err.response?.data?.error || err.message));
    } finally {
      setWorking(null);
    }
  };

  const handleReject = async (id) => {
    if (!window.confirm('Reject (delete) this submission?')) return;
    setWorking(id);
    try {
      await pendingRecipes.reject(id);
      await load();
    } catch (err) {
      alert('Reject failed: ' + (err.response?.data?.error || err.message));
    } finally {
      setWorking(null);
    }
  };

  return (
    <div className="max-w-5xl mx-auto px-4 py-6">
      <h1 className="text-2xl font-bold mb-2">Pending public submissions</h1>
      <p className="text-sm text-gray-600 mb-6">
        Anonymous recipe contributions awaiting maintainer review. Approve to add to your
        kitchen + the public catalog; reject to delete.
      </p>

      {loading && <div className="text-gray-500">Loading…</div>}
      {error && <div className="text-red-600 mb-4">{error}</div>}

      {!loading && items.length === 0 && (
        <div className="text-gray-500 p-8 text-center border border-dashed border-gray-300 rounded">
          🎉 Nothing pending.
        </div>
      )}

      <ul className="space-y-3">
        {items.map((r) => (
          <li key={r.id} className="bg-white border border-gray-200 rounded shadow-sm p-4">
            <div className="flex items-start justify-between gap-4">
              <div className="flex-1 min-w-0">
                <h2 className="text-lg font-semibold text-gray-900">{r.title}</h2>
                {r.description && (
                  <p className="text-sm text-gray-700 mt-1 line-clamp-2">{r.description}</p>
                )}
                <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-xs text-gray-500">
                  <span>{r.ingredient_count} ingredient{r.ingredient_count === 1 ? '' : 's'}</span>
                  {r.source_url && (
                    <a href={r.source_url} target="_blank" rel="noreferrer" className="text-indigo-600 hover:text-indigo-800">
                      source
                    </a>
                  )}
                  {r.submitter_name && <span>by {r.submitter_name}</span>}
                  {r.submitter_email && <span>{r.submitter_email}</span>}
                  <span>{new Date(r.created_at).toLocaleString()}</span>
                </div>
              </div>
              <div className="flex flex-col gap-2 shrink-0">
                <button
                  type="button"
                  onClick={() => handleApprove(r.id)}
                  disabled={working === r.id}
                  className="px-3 py-1.5 text-sm bg-green-600 text-white rounded hover:bg-green-700 disabled:opacity-50"
                >
                  {working === r.id ? 'Working…' : 'Approve'}
                </button>
                <button
                  type="button"
                  onClick={() => handleReject(r.id)}
                  disabled={working === r.id}
                  className="px-3 py-1.5 text-sm border border-red-300 text-red-700 rounded hover:bg-red-50 disabled:opacity-50"
                >
                  Reject
                </button>
              </div>
            </div>
          </li>
        ))}
      </ul>

      <div className="mt-6">
        <Link to="/app" className="text-indigo-600 hover:text-indigo-800 text-sm">&larr; Back to app</Link>
      </div>
    </div>
  );
}
